<script setup>
import { reactive, computed, watch, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import LogoUpload from '../knowledgeBase/LogoUpload.vue';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['edit', 'create'].includes(value),
  },
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('pilotAssistants/getUIFlags'),
};

const initialState = {
  name: '',
  description: '',
  productName: '',
  // TODO: 功能暂时禁用，后续开放
  // featureMemory: false,
  // featureCitation: false,
  avatarFile: null,
  avatarUrl: '',
};

const state = reactive({ ...initialState });

// LogoUpload 组件引用
const logoUploadRef = ref(null);

const validationRules = {
  name: { required, minLength: minLength(1) },
  description: { required, minLength: minLength(1) },
  // productName 不必填，后端会使用默认值 "this product"
};

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() => formState.uiFlags.value.creatingItem);

const getErrorMessage = (field, errorKey) => {
  // 检查字段是否在验证规则中，避免访问未定义的字段
  if (!v$.value[field]) return '';
  return v$.value[field].$error
    ? t(`PILOT.ASSISTANTS.FORM.${errorKey}.ERROR`)
    : '';
};

const formErrors = computed(() => ({
  name: getErrorMessage('name', 'NAME'),
  description: getErrorMessage('description', 'DESCRIPTION'),
  // productName 已移除必填验证，不需要错误提示
}));

const handleCancel = () => emit('cancel');

const prepareAssistantDetails = () => {
  const formData = new FormData();

  // 添加基本字段
  formData.append('assistant[name]', state.name);
  formData.append('assistant[description]', state.description);

  // 添加 avatar 文件
  if (state.avatarFile) {
    formData.append('assistant[avatar]', state.avatarFile);
  }

  // 添加 config
  formData.append('assistant[config][product_name]', state.productName);
  // TODO: 功能暂时禁用，后续开放
  // formData.append('assistant[config][feature_memory]', state.featureMemory);
  // formData.append('assistant[config][feature_citation]', state.featureCitation);

  return formData;
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }

  emit('submit', prepareAssistantDetails());
};

const updateStateFromAssistant = assistant => {
  if (!assistant) return;

  const { name, description, config, avatar_url } = assistant;

  Object.assign(state, {
    name,
    description,
    productName: config?.product_name || '',
    // TODO: 功能暂时禁用，后续开放
    // featureMemory: config?.feature_memory || false,
    // featureCitation: config?.feature_citation || false,
    avatarUrl: avatar_url || '',
  });
};

const resetForm = () => {
  Object.assign(state, initialState);
  // 重置 LogoUpload 组件状态
  nextTick(() => {
    logoUploadRef.value?.reset();
  });
};

watch(
  () => props.assistant,
  newAssistant => {
    if (props.mode === 'edit' && newAssistant) {
      updateStateFromAssistant(newAssistant);
    }
  },
  { immediate: true }
);

// 暴露 resetForm 方法供父组件调用
defineExpose({ resetForm });
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <!-- Logo 上传 -->
    <LogoUpload
      ref="logoUploadRef"
      v-model="state.avatarFile"
      :label="t('PILOT.ASSISTANTS.FORM.AVATAR.LABEL')"
      :existing-logo-url="state.avatarUrl"
      translation-prefix="PILOT.ASSISTANTS.FORM.AVATAR"
    />

    <Input
      v-model="state.name"
      :label="t('PILOT.ASSISTANTS.FORM.NAME.LABEL')"
      :placeholder="t('PILOT.ASSISTANTS.FORM.NAME.PLACEHOLDER')"
      :message="formErrors.name"
      :message-type="formErrors.name ? 'error' : 'info'"
    />

    <Editor
      v-model="state.description"
      :label="t('PILOT.ASSISTANTS.FORM.DESCRIPTION.LABEL')"
      :placeholder="t('PILOT.ASSISTANTS.FORM.DESCRIPTION.PLACEHOLDER')"
      :message="formErrors.description"
      :message-type="formErrors.description ? 'error' : 'info'"
    />

    <!-- 暂时隐藏产品名称字段 -->
    <!-- <Input
      v-model="state.productName"
      :label="t('PILOT.ASSISTANTS.FORM.PRODUCT_NAME.LABEL')"
      :placeholder="t('PILOT.ASSISTANTS.FORM.PRODUCT_NAME.PLACEHOLDER')"
      :message="formErrors.productName || ''"
      :message-type="formErrors.productName ? 'error' : 'info'"
    /> -->

    <!-- TODO: 功能开关暂时禁用，后续开放 -->
    <!-- <fieldset class="flex flex-col gap-2.5">
      <legend class="mb-3 text-sm font-medium text-n-slate-12">
        {{ t('PILOT.ASSISTANTS.FORM.FEATURES.TITLE') }}
      </legend>

      <label class="flex items-center gap-2">
        <input v-model="state.featureMemory" type="checkbox" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('PILOT.ASSISTANTS.FORM.FEATURES.ALLOW_MEMORIES') }}
        </span>
      </label>

      <label class="flex items-center gap-2">
        <input v-model="state.featureCitation" type="checkbox" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('PILOT.ASSISTANTS.FORM.FEATURES.ALLOW_CITATIONS') }}
        </span>
      </label>
    </fieldset> -->

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('PILOT.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t(`PILOT.FORM.${mode.toUpperCase()}`)"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
